import { useMemo } from 'react';
import { MessageCircle } from 'lucide-react';
import { trackWhatsAppClick, trackWhatsAppRedirect, flushEvents } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';

import { WHATSAPP_NUMBER, SENDER_KEYWORD } from '../lib/brand';

const FALLBACK_PHONE = WHATSAPP_NUMBER;

function isMobile() {
  if (typeof navigator === 'undefined') return false;
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (typeof window !== 'undefined' &&
      window.matchMedia != null &&
      window.matchMedia('(pointer: coarse)').matches)
  );
}

function buildWhatsAppUrl(phone: string, message: string, desktop = false): string {
  if (desktop) {
    // Desktop: go straight to WhatsApp Web so we never hit the
    // "whatsapp:// scheme not registered" error on PCs without the app.
    return `https://web.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
  }
  // Mobile / universal fallback: wa.me is WhatsApp's official deeplink.
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

interface WhatsAppDeeplinkButtonProps {
  orderNum: string;
  message?: string;
  variant?: 'primary' | 'outline' | 'large';
  onClick?: () => void;
  disabled?: boolean;
}

export function generateOrderDeeplink(orderNum: string, phone = FALLBACK_PHONE): string {
  const message = `${SENDER_KEYWORD}落單 ORDER:${orderNum}`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

export default function WhatsAppDeeplinkButton({
  orderNum,
  variant = 'primary',
  onClick,
  disabled = false,
}: WhatsAppDeeplinkButtonProps) {
  const { getValue } = useCMS();
  const phone = getValue('site.whatsapp.number', FALLBACK_PHONE) || FALLBACK_PHONE;
  const message = useMemo(() => `${SENDER_KEYWORD}落單 ORDER:${orderNum}`, [orderNum]);
  const mobile = useMemo(() => isMobile(), []);
  const deepLink = useMemo(
    () => buildWhatsAppUrl(phone, message, !mobile),
    [phone, message, mobile]
  );

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    trackWhatsAppClick(orderNum, 'deeplink');
    // 旅程漏斗步驟：跳轉確認訂單（journey-automation 彙總用）
    trackWhatsAppRedirect('order_confirm', orderNum);

    if (mobile) {
      // Mobile browsers often block target="_blank" as a popup.
      // Use same-window navigation so the deeplink reliably fires.
      e.preventDefault();
      flushEvents();
      window.location.href = deepLink;
      // If the page stays (e.g. fallback), let parent know after navigation.
      setTimeout(() => onClick?.(), 0);
      return;
    }

    // Desktop: let the native anchor open web.whatsapp.com in a new tab,
    // then tell the parent so it can disable the button.
    flushEvents();
    setTimeout(() => onClick?.(), 0);
  };

  const baseClasses = 'inline-flex items-center justify-center gap-2 font-bold rounded-2xl transition';
  const variantClasses = {
    primary: 'bg-[#25D366] text-white px-6 py-3 hover:bg-[#128C7E] shadow-lg',
    outline: 'border-2 border-[#25D366] text-[#25D366] px-6 py-3 hover:bg-[#25D366] hover:text-white',
    large: 'w-full bg-[#25D366] text-white text-lg px-6 py-4 hover:bg-[#128C7E] shadow-lg',
  };
  const disabledClasses = 'opacity-50 cursor-not-allowed grayscale';

  return (
    <a
      href={deepLink}
      target={mobile ? undefined : '_blank'}
      rel={mobile ? undefined : 'noopener noreferrer'}
      onClick={handleClick}
      aria-disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${disabled ? disabledClasses : 'active:scale-95'}`}
    >
      <MessageCircle size={24} />
      在 WhatsApp 確認訂單
    </a>
  );
}
